import React, { useState } from 'react'
import { components } from '@plandi/common/src/generated'
import Typography from '@material-ui/core/Typography'
import {
  dateFormatMask,
  format,
  moneyFormat,
  TranslationDictionary,
  Truncate,
  useDisclosure,
  useLocale
} from '@plandi/common'
import TabContext from '@material-ui/lab/TabContext'
import TabList from '@material-ui/lab/TabList'
import Tab from '@material-ui/core/Tab'
import TabPanel from '@material-ui/lab/TabPanel'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'
import clsx from 'clsx'
import { useRouter } from 'next/router'

import { CarouselModal, URLS } from 'src/common'
import { useProjectViewStyles } from './project-view.styles'
import { ProjectViewContactSellerButton } from './project-view-contact-seller-button'
import { useQueryProjectCategories } from '../project-queries'
import { ProjectAuthorMenu } from '../project-author-menu'
import Image from 'next/image'

enum TabProject {
  APPOINTMENT = 'appointment',
  SPECS = 'specs',
  PROJECT_COMPOSITION = 'projectComposition',
  NOTES = 'notes',
  IMAGES = 'images'
}

type ProjectViewProps = {
  name?: string | null
  cost?: number | null
  isNegotiated?: boolean
  designYear?: number | null
  categoryId?: number | null
  subCategoryId?: number | null
  projectStaging?: components['schemas']['ProjectStagedDesing'] | null
  expertise?: boolean | null
  bimModel?: boolean | null
  fileFormats?: string[] | null
  landArea?: number | null
  builtUpArea?: number | null
  buildingArea?: number | null
  buildingHeight?: number | null
  numberOfFloors?: number | null
  appointment?: string | null
  projectComposition?: string | null
  renderUserNameAndImg?: () => JSX.Element
  projectStatus?: components['schemas']['ProjectMarketStatus']
  notes?: string | null
  createdAt?: string
  isPreviewMode?: boolean
  isAuthor?: boolean
  parts?: string[]
  cover?: string
  images?: string[]
  category?: string
  subCategory?: string
  type?: string
  profile?: {
    id?: number
    name?: string
    fullName?: string
    avatarUrl?: string
    slug_name?: string
  }
  id?: number
  translate?: TranslationDictionary['data']
  locale: string
}

export const ProjectView: React.FC<ProjectViewProps> = (props) => {
  const classes = useProjectViewStyles()
  const router = useRouter()
  const [tab, setTab] = useState(TabProject.APPOINTMENT)
  const imagesDisclosure = useDisclosure()
  const coverDisclosure = useDisclosure()
  const [activeIndex, setActiveIndex] = useState<number | undefined>()
  const locale = useLocale()
  const { data: category } = useQueryProjectCategories({
    select: (categories) =>
      categories?.find((category) => category.id === props.categoryId),
    enabled: !!props.categoryId
  })

  const subCategory = category?.subCategories?.find(
    (subCategory) => subCategory.id === props.subCategoryId
  )

  const partText = (props.parts ?? [])?.join(', ')

  const coverImageCarousel = props.cover ? [props.cover] : []

  const renderImage = () => {
    if (props.cover) {
      return (
        <>
          <div className={classes.img}>
            <Image
              width={450}
              height={448}
              src={props.cover}
              alt={props.name ?? ''}
              className={classes.img}
              onClick={coverDisclosure.onOpen}
              role="presentation"
            />
          </div>
          <CarouselModal
            isOpen={coverDisclosure.isOpen}
            onClose={coverDisclosure.onClose}
            images={coverImageCarousel}
          />
        </>
      )
    }

    return (
      <div className={clsx(classes.img, classes.imgIcon)}>
        <InsertDriveFileIcon color="action" fontSize="large" />
      </div>
    )
  }

  const renderButton = () => {
    if (props.isPreviewMode) {
      return null
    }

    if (props.isAuthor) {
      return (
        <ProjectAuthorMenu
          projectId={props.id}
          deleteMutationOptions={{
            onSuccess: () => router.push(URLS.home)
          }}
          status={props.projectStatus}
          slugName={router.query.slugName as string}
          translate={props.translate}
        />
      )
    }

    return (
      <ProjectViewContactSellerButton
        type={props.type}
        profile={props.profile}
        projectId={props.id}
        slugName={router.query.slugName as string}
        translate={props.translate}
      />
    )
  }

  const handleCloseCarousel = () => {
    imagesDisclosure.onClose()
    setActiveIndex(undefined)
  }

  const handleOpenCarousel = (index: number) => () => {
    imagesDisclosure.onOpen()
    setActiveIndex(index)
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" component="h1" className={classes.projectName}>
          {props.name}
        </Typography>
        {props.isPreviewMode ? (
          ''
        ) : (
          <div className={classes.favoritBtn}>
            {props.renderUserNameAndImg && props.renderUserNameAndImg()}
          </div>
        )}
        {props.isNegotiated ? (
          <Typography variant="h5" className={classes.price}>
            {props.locale === 'ru' ? 'Договорная цена' : 'Negotiated price'}
          </Typography>
        ) : (
          <Typography variant="h5" className={classes.price}>
            {props.cost && moneyFormat(props.cost, true, locale)}
          </Typography>
        )}
        {renderButton()}
      </div>
      <div className={classes.mainProjectInfo}>
        {renderImage()}
        <div className={classes.aboutProject}>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {`${props.translate?.category}:`}
            </Typography>
            <Typography variant="subtitle2" className={classes.wordBreak}>
              {category?.name ?? props.category}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {`${props.translate?.subcategory}:`}
            </Typography>
            <Typography variant="subtitle2" className={classes.wordBreak}>
              {subCategory?.name ?? props.subCategory}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {`${props.translate?.shop['project-page']['parts']}:`}
            </Typography>
            <div>
              <Truncate
                variant="subtitle2"
                className={classes.wordBreak}
                translate={props.translate}
              >
                {partText}
              </Truncate>
            </div>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {`${props.translate?.shop['project-page']['year-design']}:`}
            </Typography>
            <Typography variant="subtitle2">
              {props.designYear
                ? props.designYear
                : props.translate?.['not-specified-lower']}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {`${props.translate?.shop['project-page']['expertise']}:`}
            </Typography>
            <Typography variant="subtitle2">
              {props.expertise
                ? props.translate?.present
                : props.translate?.missing}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {`${props.translate?.shop['project-page']['bim-model']}:`}
            </Typography>
            <Typography variant="subtitle2">
              {props.bimModel
                ? props.translate?.present
                : props.translate?.missing}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {`${props.translate?.shop['project-page']['file-format']}:`}
            </Typography>
            <Typography variant="subtitle2" className={classes.wordBreak}>
              {props.fileFormats
                ? props.fileFormats?.join(', ')
                : props.translate?.['not-specified']}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {`${props.translate?.shop['project-page']['date-publish']}:`}
            </Typography>
            <Typography variant="subtitle2">
              {props.createdAt &&
                dateFormatMask(props.createdAt, format.shortFullDate)}
            </Typography>
          </div>
        </div>
      </div>
      <TabContext value={tab}>
        <TabList
          textColor="secondary"
          onChange={(_, value) => setTab(value)}
          TabIndicatorProps={{ className: classes.noneIndicator }}
          classes={{ flexContainer: classes.tabFlexContainer }}
        >
          <Tab
            label={props.translate?.shop['project-page']['appointment']}
            value={TabProject.APPOINTMENT}
            className={clsx(classes.tab, {
              [classes.tabActive]: tab === TabProject.APPOINTMENT
            })}
          />
          <Tab
            label={props.translate?.shop['project-page']['specifications']}
            value={TabProject.SPECS}
            className={clsx(classes.tab, {
              [classes.tabActive]: tab === TabProject.SPECS
            })}
          />
          <Tab
            label={props.translate?.shop['project-page']['composition-project']}
            value={TabProject.PROJECT_COMPOSITION}
            className={clsx(classes.tab, {
              [classes.tabActive]: tab === TabProject.PROJECT_COMPOSITION
            })}
          />
          <Tab
            label={props.translate?.shop['project-page']['notes']}
            value={TabProject.NOTES}
            className={clsx(classes.tab, {
              [classes.tabActive]: tab === TabProject.NOTES
            })}
          />
          <Tab
            label={props.translate?.shop['project-page']['images']}
            value={TabProject.IMAGES}
            className={clsx(classes.tab, {
              [classes.tabActive]: tab === TabProject.IMAGES
            })}
          />
        </TabList>
        <TabPanel value={TabProject.APPOINTMENT} className={classes.tabPanel}>
          <Typography variant="body1" className={classes.wordBreak}>
            <div
              dangerouslySetInnerHTML={{
                __html: props.appointment ? props.appointment : ' '
              }}
            />
          </Typography>
        </TabPanel>
        <TabPanel value={TabProject.SPECS} className={classes.tabPanel}>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {
                //eslint-disable-next-line max-len
                `${props.translate?.shop['add-project-page']['specifications']['plot-area']}:`
              }
            </Typography>
            <Typography variant="subtitle2">
              {props.landArea ? (
                <>
                  {props.landArea} {`${props.locale === 'ru' ? 'м' : 'm'}`}
                  <sup className={classes.sup}>2</sup>
                </>
              ) : (
                props.translate?.['not-specified']
              )}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {
                //eslint-disable-next-line max-len
                `${props.translate?.shop['add-project-page']['specifications']['building-area']}:`
              }
            </Typography>
            <Typography variant="subtitle2">
              {props.builtUpArea ? (
                <>
                  {props.builtUpArea} {`${props.locale === 'ru' ? 'м' : 'm'}`}
                  <sup className={classes.sup}>2</sup>
                </>
              ) : (
                props.translate?.['not-specified-lower']
              )}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {
                //eslint-disable-next-line max-len
                `${props.translate?.shop['add-project-page']['specifications']['building-home']}:`
              }
            </Typography>
            <Typography variant="subtitle2">
              {props.buildingArea ? (
                <>
                  {props.buildingArea} {`${props.locale === 'ru' ? 'м' : 'm'}`}
                  <sup className={classes.sup}>2</sup>
                </>
              ) : (
                props.translate?.['not-specified-lower']
              )}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {
                //eslint-disable-next-line max-len
                `${props.translate?.shop['add-project-page']['specifications']['building-height']}:`
              }
            </Typography>
            <Typography variant="subtitle2">
              {props.buildingHeight
                ? `${props.buildingHeight} ${props.locale === 'ru' ? 'м' : 'm'}`
                : props.translate?.['not-specified-lower']}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classes.title}
            >
              {
                //eslint-disable-next-line max-len
                `${props.translate?.shop['add-project-page']['specifications']['number-of-floors']}:`
              }
            </Typography>
            <Typography variant="subtitle2">
              {props.numberOfFloors
                ? props.numberOfFloors
                : props.translate?.['not-specified-lower']}
            </Typography>
          </div>
        </TabPanel>
        <TabPanel
          value={TabProject.PROJECT_COMPOSITION}
          className={classes.tabPanel}
        >
          <Typography
            variant="body1"
            className={clsx(classes.wordBreak, classes.textWhiteSpace)}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: props.projectComposition ? props.projectComposition : ''
              }}
            />
          </Typography>
        </TabPanel>
        <TabPanel value={TabProject.NOTES} className={classes.tabPanel}>
          <Typography
            variant="body1"
            className={clsx(classes.wordBreak, classes.textWhiteSpace)}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: props.notes ? props.notes : ''
              }}
            />
          </Typography>
        </TabPanel>
        <TabPanel value={TabProject.IMAGES} className={classes.tabPanel}>
          {props.images?.length && (
            <>
              <div className={classes.imageList}>
                {props.images?.map((image, index) => (
                  <Image
                    width={300}
                    height={150}
                    key={index}
                    src={image}
                    alt={image}
                    className={classes.image}
                    onClick={handleOpenCarousel(index)}
                    role="presentation"
                  />
                ))}
              </div>
              <CarouselModal
                images={props.images}
                isOpen={imagesDisclosure.isOpen}
                onClose={handleCloseCarousel}
                activeIndex={activeIndex}
              />
            </>
          )}
        </TabPanel>
      </TabContext>
    </div>
  )
}
